import React from "react";
// Pull-to-Refresh für Mobilgeräte muss in die Komponente verschoben werden
import Calendar from "react-lightweight-calendar";
import { de } from "date-fns/locale";
import { useSnackbar } from "notistack";
import DateDialog from "./DateDialog";
import UrlaubModal from "../components/UrlaubModal";
import SchulferienModal from "../components/SchulferienModal";
import { ferienZeitraeume } from "../constants/legendItems";
import "./../css/Calendar.css";
import Legend from "../components/Legend";
import EventListItem from "../components/EventListItem";

// Berechnung von Ostern nach Gauß-Algorithmus
const getEasterDate = (year: number) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};

// Feiertage für Sachsen generieren
const getFeiertageSachsen = (year: number) => {
  const easter = getEasterDate(year);
  const feiertage = [];

  // Feste Feiertage
  feiertage.push({ date: new Date(year, 0, 1), name: "Neujahr" });
  feiertage.push({ date: new Date(year, 4, 1), name: "Tag der Arbeit" });
  feiertage.push({
    date: new Date(year, 9, 3),
    name: "Tag der Deutschen Einheit",
  });
  feiertage.push({ date: new Date(year, 9, 31), name: "Reformationstag" });
  feiertage.push({ date: new Date(year, 11, 25), name: "1. Weihnachtstag" });
  feiertage.push({ date: new Date(year, 11, 26), name: "2. Weihnachtstag" });

  // Bewegliche Feiertage (relativ zu Ostern)
  const karfreitag = new Date(easter);
  karfreitag.setDate(easter.getDate() - 2);
  feiertage.push({ date: karfreitag, name: "Karfreitag" });

  const ostermontag = new Date(easter);
  ostermontag.setDate(easter.getDate() + 1);
  feiertage.push({ date: ostermontag, name: "Ostermontag" });

  const christiHimmelfahrt = new Date(easter);
  christiHimmelfahrt.setDate(easter.getDate() + 39);
  feiertage.push({ date: christiHimmelfahrt, name: "Christi Himmelfahrt" });

  const pfingstmontag = new Date(easter);
  pfingstmontag.setDate(easter.getDate() + 50);
  feiertage.push({ date: pfingstmontag, name: "Pfingstmontag" });

  const fronleichnam = new Date(easter);
  fronleichnam.setDate(easter.getDate() + 60);
  feiertage.push({ date: fronleichnam, name: "Fronleichnam" });

  // Buß- und Bettag (Mittwoch vor dem 23. November)
  const bussBettag = new Date(year, 10, 23);
  while (bussBettag.getDay() !== 3) {
    bussBettag.setDate(bussBettag.getDate() - 1);
  }
  feiertage.push({ date: bussBettag, name: "Buß- und Bettag" });

  return feiertage.map((feiertag, index) => {
    const year = feiertag.date.getFullYear();
    const month = String(feiertag.date.getMonth() + 1).padStart(2, "0");
    const day = String(feiertag.date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;

    return {
      id: `feiertag-${year}-${index}`,
      startTime: `${dateStr}T00:00`,
      endTime: `${dateStr}T23:59`,
      title: feiertag.name,
      bgColor: "#26a65b",
      textColor: "white",
      ganzTaegig: 1,
      wiederholen: "nie",
      isFeiertag: true,
    };
  });
};

// Schulferien für Sachsen
const getSchulferienSachsen = () => {
  // Offizielle Schulferien für Sachsen 2024-2027

  const ferienEvents: any[] = [];

  ferienZeitraeume.forEach((ferien, index) => {
    const startDate = new Date(ferien.start);
    const endDate = new Date(ferien.end);

    // Erstelle einen Event pro Ferientag
    let currentDate = new Date(startDate);
    let dayIndex = 0;

    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split("T")[0];

      ferienEvents.push({
        id: `schulferien-${index}-${dayIndex}`,
        startTime: `${dateStr}T00:00`,
        endTime: `${dateStr}T23:59`,
        title: ferien.name,
        bgColor: "#3498db",
        textColor: "white",
        ganzTaegig: 1,
        wiederholen: "nie",
        isSchulferien: true,
      });

      currentDate.setDate(currentDate.getDate() + 1);
      dayIndex++;
    }
  });

  return ferienEvents;
};

export type ViewType = "MONTH" | "WEEK" | "DAY";

interface MyCalendarProps {
  currentView: ViewType;
  setCurrentView: (view: ViewType) => void;
  onDataChange?: (data: any[]) => void;
  searchSelectedEvent?: any | null;
  onSearchEventHandled?: () => void;
  refreshTrigger?: number;
  showSchulferien: boolean;
}

const MyCalendar = ({
  currentView,
  setCurrentView,
  onDataChange,
  searchSelectedEvent,
  onSearchEventHandled,
  refreshTrigger,
  showSchulferien,
}: MyCalendarProps) => {
  const heute = new Date();
  const [currentDate, setCurrentDate] = React.useState(heute.toISOString());
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(null);
  const [selectedEvent, setSelectedEvent] = React.useState<any>(null);
  const [data, setData] = React.useState<any[]>([]);
  const [showUrlaubModal, setShowUrlaubModal] = React.useState(false);
  const [showFerienModal, setShowFerienModal] = React.useState(false);
  const [selectedFilter, setSelectedFilter] = React.useState<string | null>(
    null
  );
  const { enqueueSnackbar } = useSnackbar();
  const birthdayCheckDone = React.useRef(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  // Pull-to-Refresh für Mobilgeräte (in Komponente!)
  const touchStartY = React.useRef(0);
  const pullThreshold = 80;
  const [pullDistance, setPullDistance] = React.useState(0);
  const [isPulling, setIsPulling] = React.useState(false);

  // Swipe für Monatswechsel
  const touchStartX = React.useRef(0);
  const swipeThreshold = 60; // px

  React.useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      if (window.scrollY === 0) {
        touchStartY.current = e.touches[0].clientY;
        setIsPulling(true);
      }
      // Swipe X
      touchStartX.current = e.touches[0].clientX;
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (window.scrollY > 0) return;
      const touchY = e.touches[0].clientY;
      const dist = touchY - touchStartY.current;
      if (dist > 0) {
        setPullDistance(dist);
      }
      if (dist > 0 && dist < pullThreshold * 1.5) {
        e.preventDefault();
      }
    };
    const handleTouchEnd = (e: TouchEvent) => {
      if (window.scrollY > 0) return;
      const touchY = e.changedTouches[0].clientY;
      const dist = touchY - touchStartY.current;
      setIsPulling(false);
      setPullDistance(0);
      if (dist > pullThreshold) {
        window.location.reload();
      }
      touchStartY.current = 0;

      // Swipe X auswerten
      const touchEndX = e.changedTouches[0].clientX;
      const distX = touchEndX - touchStartX.current;
      if (Math.abs(distX) > swipeThreshold) {
        // Aktuelles Datum als Date-Objekt
        const dateObj = new Date(currentDate);
        if (distX > 0) {
          // Nach rechts: nächster Monat
          dateObj.setMonth(dateObj.getMonth() + 1);
        } else {
          // Nach links: vorheriger Monat
          dateObj.setMonth(dateObj.getMonth() - 1);
        }
        setCurrentDate(dateObj.toISOString());
      }
      touchStartX.current = 0;
    };
    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: false });
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [currentDate]);

  // selectedFilter aus localStorage laden
  React.useEffect(() => {
    const savedFilter = localStorage.getItem("calendar_selectedFilter");
    if (savedFilter) {
      setSelectedFilter(savedFilter);
    }
  }, []);

  // selectedFilter in localStorage speichern
  React.useEffect(() => {
    if (selectedFilter) {
      localStorage.setItem("calendar_selectedFilter", selectedFilter);
    } else {
      localStorage.removeItem("calendar_selectedFilter");
    }
  }, [selectedFilter]);

  // Berechne kontrastreiche Textfarbe basierend auf Hintergrundfarbe
  const getTextColorForBackground = (bgColor: string): string => {
    // Konvertiere Hex zu RGB
    const hex = bgColor.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;

    // Berechne relative Luminanz nach WCAG-Standard
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

    // Wenn Luminanz < 0.5, verwende weiß, sonst schwarz
    return luminance < 0.5 ? "white" : "black";
  };

  const checkUpcomingReminders = React.useCallback(
    (events: any[]) => {
      if (birthdayCheckDone.current) return;
      birthdayCheckDone.current = true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const in7Days = new Date(today);
      in7Days.setDate(today.getDate() + 7);

      const upcomingReminders = events.filter((event) => {
        // Nur Termine mit aktiviertem "erinnern" Flag
        if (!event.erinnern || event.erinnern !== 1) return false;

        const eventDate = new Date(event.startTime);
        eventDate.setHours(0, 0, 0, 0);

        return eventDate >= today && eventDate <= in7Days;
      });

      if (upcomingReminders.length > 0) {
        upcomingReminders.forEach((reminder) => {
          const eventDate = new Date(reminder.startTime);
          eventDate.setHours(0, 0, 0, 0);

          const daysUntil = Math.round(
            (eventDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
          );

          let message = "";
          if (daysUntil === 0) {
            message = `📌 Heute: ${reminder.title}`;
          } else if (daysUntil === 1) {
            message = `⏰ Morgen: ${reminder.title}`;
          } else {
            message = `🔔 In ${daysUntil} Tagen: ${reminder.title}`;
          }

          enqueueSnackbar(message, {
            variant: "info",
            autoHideDuration: 6000,
          });
        });
      }
    },
    [enqueueSnackbar]
  );

  // Generiere wiederkehrende Termine
  const generateRecurringEvents = (events: any[]) => {
    const allEvents: any[] = [];
    const currentYear = new Date().getFullYear();
    const yearRange = 10; // Generiere für 10 Jahre in die Vergangenheit und Zukunft

    // Hilfsfunktion zum Formatieren ohne Zeitzone-Konvertierung
    const formatDateTime = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    // Durchlaufe alle Ereignisse und generiere Wiederholungen
    events.forEach((event) => {
      if (event.wiederholen === "jaehrlich") {
        // Hole das ursprüngliche Datum
        const originalStart = new Date(event.startTime);
        const originalEnd = new Date(event.endTime);
        const originalYear = originalStart.getFullYear();
        const startMonth = originalStart.getMonth();
        const startDay = originalStart.getDate();
        const startHour = originalStart.getHours();
        const startMinute = originalStart.getMinutes();
        const endMonth = originalEnd.getMonth();
        const endDay = originalEnd.getDate();
        const endHour = originalEnd.getHours();
        const endMinute = originalEnd.getMinutes();

        // Generiere Termine für vergangene und zukünftige Jahre
        for (
          let year = currentYear - yearRange;
          year <= currentYear + yearRange;
          year++
        ) {
          // Überspringe Jahre vor dem ursprünglichen Termin
          if (year < originalYear) continue;

          const newStart = new Date(
            year,
            startMonth,
            startDay,
            startHour,
            startMinute
          );
          const newEnd = new Date(year, endMonth, endDay, endHour, endMinute);

          allEvents.push({
            ...event,
            id: `${event.id}-year-${year}`,
            startTime: formatDateTime(newStart),
            endTime: formatDateTime(newEnd),
          });
        }
      } else if (event.wiederholen === "monatlich") {
        // Generiere für 12 Monate
        const originalStart = new Date(event.startTime);
        const originalEnd = new Date(event.endTime);
        const startDay = originalStart.getDate();
        const startHour = originalStart.getHours();
        const startMinute = originalStart.getMinutes();
        const endDay = originalEnd.getDate();
        const endHour = originalEnd.getHours();
        const endMinute = originalEnd.getMinutes();

        for (let i = 0; i < 24; i++) {
          const newStart = new Date(originalStart);
          newStart.setMonth(originalStart.getMonth() + i);
          newStart.setDate(startDay);
          newStart.setHours(startHour, startMinute);

          const newEnd = new Date(originalEnd);
          newEnd.setMonth(originalEnd.getMonth() + i);
          newEnd.setDate(endDay);
          newEnd.setHours(endHour, endMinute);

          allEvents.push({
            ...event,
            id: `${event.id}-month-${i}`,
            startTime: formatDateTime(newStart),
            endTime: formatDateTime(newEnd),
          });
        }
      } else if (event.wiederholen === "woechentlich") {
        // Generiere für 52 Wochen
        const originalStart = new Date(event.startTime);
        const originalEnd = new Date(event.endTime);

        for (let i = 0; i < 52; i++) {
          const newStart = new Date(originalStart);
          newStart.setDate(originalStart.getDate() + i * 7);

          const newEnd = new Date(originalEnd);
          newEnd.setDate(originalEnd.getDate() + i * 7);

          allEvents.push({
            ...event,
            id: `${event.id}-week-${i}`,
            startTime: formatDateTime(newStart),
            endTime: formatDateTime(newEnd),
          });
        }
      } else if (event.wiederholen === "taeglich") {
        // Generiere für 365 Tage
        const originalStart = new Date(event.startTime);
        const originalEnd = new Date(event.endTime);

        for (let i = 0; i < 365; i++) {
          const newStart = new Date(originalStart);
          newStart.setDate(originalStart.getDate() + i);

          const newEnd = new Date(originalEnd);
          newEnd.setDate(originalEnd.getDate() + i);

          allEvents.push({
            ...event,
            id: `${event.id}-day-${i}`,
            startTime: formatDateTime(newStart),
            endTime: formatDateTime(newEnd),
          });
        }
      } else {
        // Kein Wiederholen oder "nie"
        allEvents.push(event);
      }
    });

    return allEvents;
  };

  const loadTermine = React.useCallback(() => {
    fetch("https://norbertspringer.de/calendar/api/getTermine.php")
      .then((response) => response.json())
      .then((data) => {
        const mappedEvents = data.map((item: any) => {
          const isGanzTaegig = item.ganzTaegig === 1 || item.ganzTaegig === "1";
          const isGeburtstag = item.bgColor === "#f39c12";
          let startTime = item.startTime.replace(" ", "T");
          let endTime = item.endTime.replace(" ", "T");

          // Wenn ganztägig, entferne die Zeitangabe (setze auf Mitternacht)
          if (isGanzTaegig) {
            startTime = startTime.split("T")[0] + "T00:00";
            // Geburtstage nur kurz anzeigen (00:00 bis 01:00) statt ganzen Tag
            if (isGeburtstag) {
              endTime = startTime.split("T")[0] + "T01:00";
            } else {
              endTime = endTime.split("T")[0] + "T23:59";
            }
          }

          return {
            id: item.idTermin,
            idUser: item.idUser,
            startTime,
            endTime,
            title: item.title,
            bgColor: item.bgColor || undefined,
            textColor: item.bgColor
              ? getTextColorForBackground(item.bgColor)
              : undefined,
            wiederholen: item.wiederholen,
            ganzTaegig: isGanzTaegig ? 1 : 0,
            erinnern: item.erinnern === 1 || item.erinnern === "1" ? 1 : 0,
          };
        });

        // Generiere wiederkehrende Termine
        const eventsWithRecurring = generateRecurringEvents(mappedEvents);

        // Feiertage für einen größeren Zeitbereich hinzufügen
        const currentYear = new Date().getFullYear();
        const allFeiertage = [];
        for (let year = currentYear - 2; year <= currentYear + 10; year++) {
          allFeiertage.push(...getFeiertageSachsen(year));
        }

        // Schulferien hinzufügen (nur wenn showSchulferien aktiviert ist)
        const schulferien = showSchulferien ? getSchulferienSachsen() : [];

        const allEvents = [
          ...eventsWithRecurring,
          ...allFeiertage,
          ...schulferien,
        ];
        setData(allEvents);
        if (onDataChange) {
          onDataChange(allEvents);
        }

        // Prüfe anstehende Termine mit Erinnerung
        checkUpcomingReminders(mappedEvents);

        if (isRefreshing) {
          setIsRefreshing(false);
          enqueueSnackbar("Kalender aktualisiert", { variant: "success" });
        }
      })
      .catch((error) => {
        console.error("Fehler beim Laden:", error);
        if (isRefreshing) {
          setIsRefreshing(false);
          enqueueSnackbar("Fehler beim Aktualisieren", { variant: "error" });
        }
      });
  }, [
    onDataChange,
    checkUpcomingReminders,
    isRefreshing,
    enqueueSnackbar,
    showSchulferien,
  ]);

  const handleCellClick = (value: any) => {
    const dateObj =
      typeof value.date === "string" ? new Date(value.date) : value.date;
    setSelectedDate(dateObj);
    setSelectedEvent(null);
    setIsDialogOpen(true);
  };

  const handleItemClick = (item: any) => {
    setSelectedEvent(item);
    setSelectedDate(null);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setSelectedDate(null);
    setSelectedEvent(null);
  };

  const handleSave = () => {
    loadTermine();
  };

  React.useEffect(() => {
    loadTermine();
  }, [loadTermine]);

  React.useEffect(() => {
    if (searchSelectedEvent) {
      setSelectedEvent(searchSelectedEvent);
      setSelectedDate(null);
      setIsDialogOpen(true);
      if (onSearchEventHandled) {
        onSearchEventHandled();
      }
    }
  }, [searchSelectedEvent, onSearchEventHandled]);

  React.useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      loadTermine();
    }
  }, [refreshTrigger, loadTermine]);

  const formatDateTime = (dateTimeStr: string, isGanzTaegig: boolean) => {
    if (isGanzTaegig) {
      return "";
    }
    const date = new Date(dateTimeStr);
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const renderItemText = (item: any) => {
    const isSmallScreen = window.innerWidth < 768;

    if (isSmallScreen) {
      return <div></div>;
    }

    const isGanzTaegig = item.ganzTaegig === 1;
    const startFormatted = formatDateTime(item.startTime, isGanzTaegig);
    const endFormatted = formatDateTime(item.endTime, isGanzTaegig);

    return (
      <div
        style={{
          fontSize: "clamp(11px, 2.5vw, 14px)",
          lineHeight: "1.3",
          padding: "2px 0",
          wordBreak: "normal",
          overflowWrap: "break-word",
          whiteSpace: "normal",
        }}
      >
        <div style={{ fontWeight: "500" }}>{item.title}</div>
        {!isGanzTaegig && (
          <div
            style={{
              fontSize: "clamp(10px, 2.2vw, 12px)",
              opacity: 0.9,
              marginTop: "2px",
            }}
          >
            {startFormatted} - {endFormatted}
          </div>
        )}
      </div>
    );
  };

  // Filtere Termine für den aktuell ausgewählten Tag
  const getEventsForSelectedDay = () => {
    const selectedDay = new Date(currentDate);
    selectedDay.setHours(0, 0, 0, 0);

    const nextDay = new Date(selectedDay);
    nextDay.setDate(nextDay.getDate() + 1);

    return data
      .filter((event) => {
        const eventStart = new Date(event.startTime);
        const eventEnd = new Date(event.endTime);

        // Normalisiere die Zeiten auf Mitternacht für den Vergleich
        eventStart.setHours(0, 0, 0, 0);
        eventEnd.setHours(0, 0, 0, 0);

        // Zeige Termin an, wenn:
        // - er am ausgewählten Tag beginnt ODER
        // - er am ausgewählten Tag endet ODER
        // - er über den ausgewählten Tag hinweg läuft (beginnt vorher, endet nachher)
        return (
          eventStart.getTime() <= selectedDay.getTime() &&
          eventEnd.getTime() >= selectedDay.getTime()
        );
      })
      .sort((a, b) => {
        // Sortiere zuerst nach Geburtstagen (Geburtstage zuerst)
        const aIsGeburtstag = a.bgColor === "#f39c12";
        const bIsGeburtstag = b.bgColor === "#f39c12";

        if (aIsGeburtstag !== bIsGeburtstag) {
          return aIsGeburtstag ? -1 : 1; // Geburtstage zuerst
        }

        // Dann sortiere nach ganztägig (nicht-ganztägige zuerst, außer Geburtstage)
        const aGanzTaegig = a.ganzTaegig === 1;
        const bGanzTaegig = b.ganzTaegig === 1;

        if (aGanzTaegig !== bGanzTaegig) {
          return aGanzTaegig ? 1 : -1; // Nicht-ganztägige Termine zuerst
        }

        // Dann nach Startzeit sortieren
        return (
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
        );
      });
  };

  const selectedDayEvents = getEventsForSelectedDay();
  const selectedDayFormatted = new Date(currentDate).toLocaleDateString(
    "de-DE",
    {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }
  );

  const handleDayClick = (
    day: string,
    event: React.MouseEvent<HTMLElement>
  ) => {
    const dateObj = new Date(day);
    setCurrentDate(dateObj.toISOString());
    // Automatisches Scrollen entfernt
  };
  return (
    <div>
      {((isRefreshing) || (isPulling && pullDistance >= pullThreshold)) && (
        <div
          className="refresh-indicator"
          style={{
            position: "fixed",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "320px",
            maxWidth: "90vw",
            height: "64px",
            marginTop: "0px",
            background: "#eaf6ff",
            color: "#007bff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            fontSize: 18,
            zIndex: 9999,
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            borderRadius: "0 0 18px 18px",
            transition: "opacity 0.2s"
          }}
        >
          {isPulling
            ? (pullDistance > pullThreshold
                ? "Seite wird aktualisiert..."
                : "Zum Aktualisieren nach unten ziehen")
            : "🔄 Wird aktualisiert..."}
        </div>
      )}

      <div className="calendar-container">
        <button
          className="floating-action-button"
          onClick={() => {
            setSelectedDate(new Date(currentDate));
            setSelectedEvent(null);
            setIsDialogOpen(true);
          }}
          title="Neuer Termin"
        >
          <span className="plus-icon">+</span>
        </button>

        <Calendar
          data={
            selectedFilter
              ? data.filter((event) => event.bgColor === selectedFilter)
              : data
          }
          currentView={currentView}
          currentDate={currentDate}
          setCurrentDate={(date) =>
            setCurrentDate(typeof date === "string" ? date : date.toISOString())
          }
          activeTimeDateField="startTime-endTime"
          weekStartsOn={1}
          locale={de}
          timeDateFormat={{ hour: "HH" }}
          onCellClick={handleCellClick}
          onItemClick={handleItemClick}
          renderItemText={renderItemText}
          onDayNumberClick={handleDayClick}
        />

        {/* Termine und Legende - mobil: unten, desktop: rechts */}
        <div className="sidebar-wrapper">
          <div id="event-list-section" className="event-list-section">
            <h3 className="event-list-title">{selectedDayFormatted}</h3>
            {selectedDayEvents.length === 0 ? (
              <>
                <p className="no-events-message">
                  Keine Termine für diesen Tag
                </p>
                <div style={{ height: "120px" }} />
              </>
            ) : (
              <div className="event-list-container">
                {selectedDayEvents.map((event) => {
                  const isGeburtstag = event.bgColor === "#f39c12";
                  const isSchulferien = event.isSchulferien === true;
                  const isFeiertag = event.isFeiertag === true;
                  const isReadOnly = isSchulferien || isFeiertag;
                  const isGanzTaegig =
                    event.ganzTaegig === 1 || event.ganzTaegig === true;
                  const startTime =
                    event.startTime?.split("T")[1]?.slice(0, 5) || "";
                  const endTime =
                    event.endTime?.split("T")[1]?.slice(0, 5) || "";
                  return (
                    <EventListItem
                      key={event.id}
                      event={event}
                      isGeburtstag={isGeburtstag}
                      isReadOnly={isReadOnly}
                      isGanzTaegig={isGanzTaegig}
                      startTime={startTime}
                      endTime={endTime}
                      onClick={handleItemClick}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* Legende für die farben */}
          <Legend
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            setShowUrlaubModal={setShowUrlaubModal}
            setShowFerienModal={setShowFerienModal}
          />
        </div>
      </div>

      <DateDialog
        isOpen={isDialogOpen}
        selectedDate={selectedDate}
        selectedEvent={selectedEvent}
        onClose={closeDialog}
        onSave={handleSave}
        selectedFilter={selectedFilter}
      />

      {/* Urlaubstermine als Tabelle im Modal */}
      {showUrlaubModal && (
        <UrlaubModal
          data={data}
          onClose={() => setShowUrlaubModal(false)}
          onEdit={(ev) => {
            setSelectedEvent(ev);
            setIsDialogOpen(true);
          }}
        />
      )}

      {/* Schulferien-Tabelle als Modal */}
      {showFerienModal && (
        <SchulferienModal
          data={data}
          onClose={() => setShowFerienModal(false)}
          onEdit={(ev) => {
            setSelectedEvent(ev);
            setIsDialogOpen(true);
          }}
        />
      )}
    </div>
  );
};
export default MyCalendar;
